import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslate } from "../context/TranslationContext";
import {
  useCreateGuidelineMutation,
  useGetGuidelineQuery,
  useUpdateGuidelineMutation,
} from "../redux/Apis/Guideline";
import usePresignedUpload from "./usePresignedUpload";

const usePostGuidelineController = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const lang = useSelector((state) => state.language.lang);
  const { t } = useTranslate("community");
  const { user } = useSelector((state) => state.authSlice);
  const [content, setContent] = useState("");
  const [imgorgif, setImgorgif] = useState("");
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isRefetching, setIsRefetching] = useState(false);
  const toastShownRef = useRef(false);
  const { data: guidelineData, isSuccess: isFetchSuccess } =
    useGetGuidelineQuery({ id, params: { lang } }, { skip: !id });
  const [createGuideline, { isSuccess: isPostSuccess, isLoading: isPosting }] =
    useCreateGuidelineMutation();

  const [
    updateGuideline,
    { isSuccess: isUpdateSuccess, isLoading: isUpdating },
  ] = useUpdateGuidelineMutation();

  const { uploadFileWithPresignedUrl } = usePresignedUpload();

  useEffect(() => {
    if (id && isFetchSuccess && guidelineData?.data?.content) {
      setContent(guidelineData?.data?.content);
      setImgorgif(guidelineData?.data?.imgorgif || "");
    }
  }, [id, isFetchSuccess, guidelineData]);

  useEffect(() => {
    if ((isPostSuccess || isUpdateSuccess) && !toastShownRef.current) {
      toastShownRef.current = true;

      toast.success(id ? t("toastPostUpdated") : t("toastPostCreated"));

      setContent("");
      setImgorgif("");
      setThumbnailFile(null);
      setPreviewUrl(null);
      setIsRefetching(true);

      if (id) {
        navigate("/community");
      }
    }
  }, [isPostSuccess, isUpdateSuccess, navigate, id, t]);

  const onChangeContent = useCallback((e) => {
    setContent(e?.target?.value);
  }, []);

  const onChangeImgorgif = useCallback(
    (e) => {
      const file = e.target.files?.[0];

      if (!file) return;

      // Check GIF duration (max 7 sec)
      if (file.type === "image/gif") {
        const gifUrl = URL.createObjectURL(file);

        const img = document.createElement("img");
        img.src = gifUrl;

        fetch(gifUrl)
          .then((res) => res.arrayBuffer())
          .then((buffer) => {
            const bytes = new Uint8Array(buffer);

            let duration = 0;
            let i = 0;

            // Parse GIF frames duration
            while (i < bytes.length) {
              // Graphic Control Extension block
              if (
                bytes[i] === 0x21 &&
                bytes[i + 1] === 0xf9 &&
                bytes[i + 2] === 0x04
              ) {
                const delay = bytes[i + 4] + (bytes[i + 5] << 8);

                // Delay is stored in hundredths of a second
                duration += delay * 10;
              }

              i++;
            }

            // Convert ms → sec
            const durationInSeconds = duration / 1000;

            if (durationInSeconds > 7) {
              toast.error(t("gifMax7Sec") || "GIF must be maximum 7 seconds");

              setThumbnailFile(null);
              setPreviewUrl(null);

              e.target.value = "";

              URL.revokeObjectURL(gifUrl);

              return;
            }

            // Valid GIF
            setThumbnailFile(file);
            setPreviewUrl(gifUrl);
          })
          .catch(() => {
            toast.error(t("gifValidationFailed") || "Failed to validate GIF");

            e.target.value = "";
          });

        return;
      }

      // Normal image upload
      setThumbnailFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    },
    [t],
  );

  const onRemoveSelectedFile = useCallback(() => {
    setThumbnailFile(null);
    setPreviewUrl(null);

    const fileInputs = document.querySelectorAll("input[type='file']");
    fileInputs.forEach((input) => {
      input.value = "";
    });
  }, []);

  const onClickPost = useCallback(async () => {
    if (!user) {
      toast.info(t("loginToPost"));
      return;
    }

    if (!content.trim()) {
      toast.error(t("toastEnterContent"));
      return;
    }

    if (!thumbnailFile && !imgorgif) {
      toast.error(t("toastSelectMedia"));
      return;
    }

    let mediaKey = imgorgif;

    if (thumbnailFile instanceof File) {
      const key = await uploadFileWithPresignedUrl(thumbnailFile);
      if (!key) return;
      mediaKey = key;
    }

    try {
      const payload = { content };
      if (mediaKey) payload.imgorgif = mediaKey;

      if (id) {
        await updateGuideline({ id, body: payload, params: { lang } }).unwrap();
      } else {
        await createGuideline({ payload, params: { lang } }).unwrap();
      }
    } catch (error) {
      console.error("Post/Update failed:", error);
      toast.error(error?.data?.message || t("toastErrorGeneric"));
    }
  }, [
    user,
    content,
    thumbnailFile,
    imgorgif,
    id,
    createGuideline,
    updateGuideline,
    uploadFileWithPresignedUrl,
    t,
  ]);

  return {
    values: {
      isLoading: isPosting || isUpdating,
      isRefetching,
      content,
      imgorgif,
      previewUrl,
      isEditing: !!id,
    },
    functions: {
      onClickPost,
      onChangeContent,
      onChangeImgorgif,
      onRemoveSelectedFile,
    },
  };
};

export default usePostGuidelineController;
